/**
 * AI Recruit - Sidebar Navigation Component
 * Main navigation sidebar with AI-powered features
 */

import React from 'react'
import { NavLink, Link, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import type { RootState } from '@/stores'
import { toggleSidebar } from '@/stores/ui/uiSlice'
import { usePermission } from '@/hooks/usePermission'
import type { Capability } from '@/config/permissions'
import Tooltip from '@/components/common/Tooltip'
import {
  LayoutDashboard,
  FileText,
  Briefcase,
  Link2,
  Bot,
  X,
  BarChart2,
} from 'lucide-react'
import clsx from 'clsx'

// `capability` mirrors each page's own RoleRoute guard in App.tsx exactly
// (dashboard.view / resumes.viewList / jobs.viewList / matching.view) —
// added 2026-08-25 so a role without the underlying capability doesn't
// see a nav item that just silently redirects it elsewhere when clicked,
// same reasoning as the Team Analytics link below, which already did
// this. jobs.view itself was split into jobs.viewList/jobs.viewDetails
// 2026-08-27, and resumes.view into resumes.viewList/viewDetails
// 2026-08-29 — both links go to their list page, so they follow viewList.
const navigation: { name: string; href: string; icon: typeof LayoutDashboard; capability: Capability }[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, capability: 'dashboard.view' },
  { name: 'Candidates', href: '/resumes', icon: FileText, capability: 'resumes.viewList' },
  { name: 'Jobs', href: '/jobs', icon: Briefcase, capability: 'jobs.viewList' },
  { name: 'Apply Matching', href: '/matching', icon: Link2, capability: 'matching.view' },
]

const Sidebar: React.FC = () => {
  const dispatch = useDispatch()
  const { sidebarOpen } = useSelector((state: RootState) => state.ui)
  const { can } = usePermission()
  const location = useLocation()

  return (
    <>
      {/* Desktop sidebar */}
      <div className={clsx(
        'fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0',
        // CHANGED: Use !sidebarOpen for logic clarity
        !sidebarOpen ? '-translate-x-full lg:w-16' : 'translate-x-0'
      )}>
      
        {/* Sidebar header */}
        <div className={clsx(
          'flex items-center h-16 px-4 border-b border-gray-200',
          !sidebarOpen && 'lg:justify-center'
        )}>
          
          {/* Link wrapper for logo and text */}
          <Link to="/dashboard" className="flex items-center overflow-hidden">
            {/* Logo */}
            <div className="flex items-center justify-center w-8 h-8 bg-gradient-primary rounded-lg flex-shrink-0">
              <Bot className="w-5 h-5 text-white" />
            </div>

            {/* Text */}
            <span className={clsx(
              'ml-3 text-xl font-bold text-gradient whitespace-nowrap transition-all duration-300',
              !sidebarOpen && 'lg:opacity-0 lg:w-0 lg:overflow-hidden'
            )}>
              AI Recruit
            </span>
          </Link>

          {/* Mobile close button */}
          <button
            onClick={() => dispatch(toggleSidebar())}
            className="p-1 rounded-lg hover:bg-gray-100 ml-auto lg:hidden"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-2">
          {navigation.filter((item) => can(item.capability)).map((item) => {
            const navLink = (
              <NavLink
                key={item.name}
                to={item.href}
                className={clsx(
                  'group flex items-center px-1 py-2 text-sm font-medium rounded-lg transition-colors duration-200',
                  location.pathname.startsWith(item.href)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                  !sidebarOpen && 'lg:justify-center'
                )}
              >
                <item.icon className={clsx(
                  'flex-shrink-0 w-5 h-5 transition-colors duration-200',
                  location.pathname.startsWith(item.href) ? 'text-primary-700' : 'text-gray-400 group-hover:text-gray-500'
                )} />
                <span className={clsx(
                  'ml-3 transition-opacity duration-300',
                  !sidebarOpen && 'lg:opacity-0 lg:w-0 lg:overflow-hidden'
                )}>
                  {item.name}
                </span>
              </NavLink>
            );

            // This logic wraps the link with YOUR Tooltip component when the sidebar is closed
            return !sidebarOpen ? (
              <Tooltip
                key={item.name}
                content={item.name}
                position="right"
                delay={0}
              >
                {navLink}
              </Tooltip>
            ) : (
              navLink // Otherwise, it just shows the link
            );
          })}

          {/* Converted 2026-08-14 (§10.1) from a raw exact `user?.role ===
              'manager'` literal onto the real backend capability —
              `analytics.teamDashboard` is itself seeded as an exact-manager
              grant (RBAC_UI_IMPLEMENTATION_PLAN.md §5.6.A: the backing
              service resolves the team by the caller's own manager_id, so
              admin/super_admin would just see an empty "No Team Assigned"
              state, not real data), so this reproduces the same exactness
              without a separate literal. */}
          {can('analytics.teamDashboard') && (() => {
            const teamAnalyticsLink = (
              <NavLink
                to="/team-analytics"
                className={({ isActive }) => clsx(
                  'group flex items-center px-1 py-2 text-sm font-medium rounded-lg transition-colors duration-200',
                  isActive
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                  !sidebarOpen && 'lg:justify-center'
                )}
              >
                <BarChart2 className={clsx(
                  'flex-shrink-0 w-5 h-5 transition-colors duration-200',
                  location.pathname.startsWith('/team-analytics') ? 'text-primary-700' : 'text-gray-400 group-hover:text-gray-500'
                )} />
                <span className={clsx(
                  'ml-3 transition-opacity duration-300',
                  !sidebarOpen && 'lg:opacity-0 lg:w-0 lg:overflow-hidden'
                )}>
                  Team Analytics
                </span>
              </NavLink>
            )

            return (
              <>
                <div className={clsx(
                  'px-2 pt-6 pb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider',
                  !sidebarOpen && 'lg:opacity-0'
                )}>
                  Analytics
                </div>
                {!sidebarOpen ? (
                  <Tooltip content="Team Analytics" position="right" delay={0}>
                    {teamAnalyticsLink}
                  </Tooltip>
                ) : teamAnalyticsLink}
              </>
            )
          })()}

        </nav>
        {/* Sidebar footer */}
        <div className={clsx(
          'p-4 border-t border-gray-200 transition-opacity duration-300',
          !sidebarOpen && 'lg:opacity-0'
        )}>
        </div>
      </div>
    </>
  )
}

export default Sidebar